import axios from "axios";
import { endpoint } from "./endpoint";
import toast from "react-hot-toast";
import { logout } from "../features/Auth/AuthSlice";
import store from "../app/store";

const TEST_BYPASS_USER_ID = 17;

const getSessionAuth = () => store.getState()?.auth || {};

let isLoggingOut = false;

export const triggerAutoLogout = (reason = "Invalid or expired token. Please log in again.") => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  sessionStorage.clear();
  localStorage.removeItem("accessToken");
  store.dispatch(logout());
  toast.error(reason, { id: "session-expired" });
  setTimeout(() => { window.location.href = "/login"; }, 100);
};

export const isTokenExpiredResponse = (responseOrError) => {
  if (!responseOrError) return false;
  const status = responseOrError.status ?? responseOrError.response?.status;
  const msg = (
    responseOrError.data?.message ||
    responseOrError.response?.data?.message ||
    responseOrError.data?.error ||
    responseOrError.response?.data?.error ||
    ""
  ).toString().toLowerCase();
  return (
    status === 401 ||
    (status === 403 && !getSessionAuth().isTestUser) ||
    Boolean(msg && /token|jwt/.test(msg) && /invalid|expired/.test(msg))
  );
};

const getLatestToken = (authToken) => {
  return (
    sessionStorage.getItem("accessToken") ||
    (typeof authToken === "string" ? authToken : authToken?.token) ||
    getSessionAuth()?.token
  );
};

const buildHeaders = ({ reqHeaders, authToken }) => {
  const sessionAuth = getSessionAuth();
  const isTestUser = Boolean(sessionAuth.isTestUser);
  const activeToken = getLatestToken(authToken);
  const hasToken = Boolean(activeToken);

  const baseHeaders = {
    "Content-Type": reqHeaders?.["Content-Type"] || "application/json",
    Accept: "application/json",
    ...reqHeaders,
  };

  if (!hasToken) {
    return baseHeaders;
  }

  if (!isTestUser) {
    return {
      ...baseHeaders,
      Authorization: "Bearer " + activeToken,
    };
  }

  const { Authorization: _authorization, authorization: _authorizationLower, ...headersWithoutAuth } = baseHeaders;

  return {
    ...headersWithoutAuth,
    "x-test-user-id": TEST_BYPASS_USER_ID,
  };
};

// Dynamic Request Interceptor: Ensure Axios reads latest token dynamically on EVERY request
axios.interceptors.request.use(
  (config) => {
    const sessionAuth = getSessionAuth();
    const token = getLatestToken();
    if (token && !sessionAuth.isTestUser) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const authInterceptor = [
  (res) => { if (isTokenExpiredResponse(res)) triggerAutoLogout(res?.data?.message); return res; },
  (err) => { if (isTokenExpiredResponse(err)) triggerAutoLogout(err?.response?.data?.message || err?.response?.data?.error); return Promise.reject(err); },
];

axios.interceptors.response.use(...authInterceptor);

async function createRequest({ reqHeaders, params, authToken }) {
  const instance = axios.create({
    baseURL: endpoint.API_BASE_URL,
    responseType: "json",
    crossdomain: true,
    headers: buildHeaders({ reqHeaders, authToken }),
    params,
  });
  instance.interceptors.response.use(...authInterceptor);
  return instance;
}

export const handleCatchBlock = (error) => {
  console.log("Something went wrong fetching apis - ", error);
};

export async function apiHandler({
  url,
  method,
  headers: reqHeaders,
  data: jsonData,
  params,
  authToken,
  noCache = false,
}) {
  try {
    const activeToken = getLatestToken(authToken);
    if (url.includes("/queue-tasks") || url.includes("/image-generation") || url.includes("/generate")) {
      console.log(`[Generation API Token] ${method} ${url}:`, activeToken);
    }
    const request = await createRequest({ reqHeaders, params, authToken: activeToken });
    let result = [];
    switch (method) {
      case "POST":
        result = await request.post(url, jsonData);
        break;

      case "PUT":
        result = await request.put(url, jsonData);
        break;

      case "PATCH":
        result = await request.patch(url, jsonData);
        break;

      case "DELETE":
        result = await request.delete(url);
        break;

      case "GET":
        result = await request.get(url, noCache
          ? {
              headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache",
                Expires: "0",
              },
              params: {
                ...params,
                _ts: Date.now(),
              },
            }
          : undefined);
        break;

      default:
        result = await request.get(url);
    }
    const { data, headers, status } = result;
    return { data, headers, status };
  } catch (error) {
    handleCatchBlock(error);

    if (error.response) {
      const { data, headers, status } = error.response;
      return { data, headers, status };
    } else {
      const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
      return {
        data: {
          error_code: isOffline ? "OFFLINE" : 500,
          error_message: isOffline
            ? "You appear to be offline. Please check your internet connection."
            : "Error in getting data",
        },
      };
    }
  }
}
